import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Check, X, GripVertical, ToggleRight, ToggleLeft, Search, AlertTriangle, Package, Trash2, Info } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';

const REF_TYPES = [
  { value: 'types_jeu', label: 'Catégories de jeu', desc: 'Stratégie, Ambiance, Familial...' },
  { value: 'publics_cibles', label: 'Publics cibles', desc: 'Enfant, Famille, Expert...' },
  { value: 'langues', label: 'Langues', desc: 'FR, EN, Multilingue...' },
  { value: 'tags_produit', label: 'Tags Produit', desc: 'Univers, Licences, Récompenses...' },
];

/* ──────── Single Row ──────── */
function RefRow({ item, onUpdate, onToggle, onHardDelete, saving, usage, isTagsProduit, tagCategories }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.value);
  const [editCategory, setEditCategory] = useState(item.category || '');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const productCount = usage || 0;

  const handleSave = () => {
    if (!editValue.trim()) return;
    const payload = { value: editValue.trim(), label: editValue.trim() };
    if (isTagsProduit && editCategory) {
      payload.category = editCategory;
    }
    onUpdate(item.id, payload);
    setEditing(false);
  };

  const handleToggle = () => {
    if (item.is_active && productCount > 0) {
      setConfirmDeactivate(true);
    } else {
      onToggle(item.id, !item.is_active);
    }
  };

  const handleDeleteClick = () => {
    if (productCount > 0) return; // Should not happen (button disabled)
    setConfirmHardDelete(true);
  };

  const catLabel = isTagsProduit && item.category
    ? (tagCategories.find(c => c.code === item.category)?.label || item.category)
    : null;

  return (
    <>
      <tr className={`${!item.is_active ? 'opacity-50 bg-gray-50' : ''} hover:bg-gray-50/80 transition-colors`} data-testid={`ref-row-${item.id}`}>
        <td className="px-3 py-2 w-8 text-center">
          <GripVertical className="h-3.5 w-3.5 text-gray-300 inline" />
        </td>
        <td className="px-3 py-2">
          {editing ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-7 text-sm w-40" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()} data-testid="ref-edit-input" />
              {isTagsProduit && (
                <select value={editCategory} onChange={e => setEditCategory(e.target.value)}
                  className="h-7 text-xs border border-gray-200 rounded px-2 bg-white" data-testid="ref-edit-category">
                  <option value="">-- Catégorie --</option>
                  {tagCategories.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleSave} data-testid="ref-edit-save">
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(false); setEditValue(item.value); setEditCategory(item.category || ''); }}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{item.value}</span>
              {catLabel && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{catLabel}</span>
              )}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-center">
          <span className={`inline-flex items-center gap-1 text-xs tabular-nums ${productCount > 0 ? 'text-gray-700 font-medium' : 'text-gray-300'}`}
            data-testid={`ref-usage-${item.id}`}>
            <Package className="h-3 w-3" />
            {productCount}
          </span>
        </td>
        <td className="px-3 py-2 text-center text-xs font-mono text-muted-foreground">{item.order}</td>
        <td className="px-3 py-2 text-center">
          <button onClick={handleToggle} className="inline-flex" data-testid={`ref-toggle-${item.id}`} disabled={saving}
            title={item.is_active ? 'Désactiver' : 'Réactiver'}>
            {item.is_active
              ? <ToggleRight className="h-5 w-5 text-emerald-500" />
              : <ToggleLeft className="h-5 w-5 text-gray-300" />}
          </button>
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(true); setEditCategory(item.category || ''); }} disabled={saving}
              data-testid={`ref-edit-${item.id}`} title="Modifier">
              <Pencil className="h-3.5 w-3.5 text-gray-400" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleDeleteClick}
              disabled={saving || productCount > 0}
              data-testid={`ref-delete-${item.id}`}
              title={productCount > 0 ? `Utilisé par ${productCount} produit${productCount > 1 ? 's' : ''} — suppression impossible` : 'Supprimer définitivement'}>
              <Trash2 className={`h-3.5 w-3.5 ${productCount > 0 ? 'text-gray-200 cursor-not-allowed' : 'text-red-400 hover:text-red-600'}`} />
            </Button>
          </div>
        </td>
      </tr>

      {/* Confirmation désactivation */}
      {confirmDeactivate && (
        <tr className="bg-amber-50 border-t border-amber-200">
          <td colSpan={6} className="px-3 py-2">
            <div className="flex items-center gap-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span className="text-amber-800">
                <strong>&laquo;{item.value}&raquo;</strong> est utilisé par <strong>{productCount} produit{productCount > 1 ? 's' : ''}</strong>.
                La désactivation masquera cette valeur des filtres et formulaires.
              </span>
              <div className="flex gap-2 ml-auto flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDeactivate(false)}>Annuler</Button>
                <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={() => { onToggle(item.id, false); setConfirmDeactivate(false); }}
                  data-testid="confirm-deactivate">
                  Désactiver quand même
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Confirmation suppression définitive */}
      {confirmHardDelete && (
        <tr className="bg-red-50 border-t border-red-200">
          <td colSpan={6} className="px-3 py-2">
            <div className="flex items-center gap-3 text-sm">
              <Trash2 className="h-4 w-4 text-red-600 flex-shrink-0" />
              <span className="text-red-800">
                Cette valeur n&apos;est utilisée par aucun produit.
                <strong> Voulez-vous la supprimer définitivement ?</strong> Cette action est irréversible.
              </span>
              <div className="flex gap-2 ml-auto flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmHardDelete(false)}>Annuler</Button>
                <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => { onHardDelete(item.id); setConfirmHardDelete(false); }}
                  data-testid="confirm-hard-delete">
                  Supprimer définitivement
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ──────── Main Page ──────── */
export default function AdminReferentiels() {
  const [activeType, setActiveType] = useState('types_jeu');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [usage, setUsage] = useState({});
  const [tagCategories, setTagCategories] = useState([]);

  const isTagsProduit = activeType === 'tags_produit';

  // Load tag categories once
  useEffect(() => {
    api.get('/referentiels/tags_produit/categories')
      .then(res => setTagCategories(res.data?.categories || []))
      .catch(() => {});
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, usageRes] = await Promise.all([
        api.get(`/referentiels/${activeType}?include_inactive=${showInactive}`),
        api.get(`/referentiels/${activeType}/usage`).catch(() => ({ data: { usage: {} } })),
      ]);
      setItems(itemsRes.data?.items || []);
      setUsage(usageRes.data?.usage || {});
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [activeType, showInactive]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { setSearchTerm(''); setNewCategory(''); }, [activeType]);

  const handleAdd = async () => {
    const val = newValue.trim();
    if (!val) return;
    if (isTagsProduit && !newCategory) {
      toast.error('La catégorie est obligatoire pour les tags produit');
      return;
    }
    setSaving(true);
    try {
      const payload = { value: val };
      if (isTagsProduit && newCategory) {
        payload.category = newCategory;
      }
      await api.post(`/referentiels/${activeType}`, payload);
      setNewValue('');
      setNewCategory('');
      toast.success(`"${val}" ajouté`);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, data) => {
    setSaving(true);
    try {
      await api.put(`/referentiels/${activeType}/${id}`, data);
      toast.success('Modifié');
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, newActive) => {
    setSaving(true);
    try {
      if (newActive) {
        await api.put(`/referentiels/${activeType}/${id}`, { is_active: true });
      } else {
        // Soft delete (deactivation)
        await api.delete(`/referentiels/${activeType}/${id}`);
      }
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleHardDelete = async (id) => {
    setSaving(true);
    try {
      await api.delete(`/referentiels/${activeType}/${id}?hard=true`);
      toast.success('Valeur supprimée définitivement');
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally {
      setSaving(false);
    }
  };

  const activeCount = items.filter(i => i.is_active).length;
  const meta = REF_TYPES.find(r => r.value === activeType);

  // Filter by search
  const filtered = searchTerm
    ? items.filter(i => i.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.category || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : items;

  // Usage resolution: for tags_produit use id, for others use value
  const getUsage = (item) => {
    if (activeType === 'tags_produit') return usage[item.id] || 0;
    return usage[item.value] || 0;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-referentiels">
        <div>
          <h1 className="text-2xl font-heading font-bold">Référentiels</h1>
          <p className="text-muted-foreground text-sm mt-1">Gérer les listes de valeurs administrables</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="ref-tabs">
          {REF_TYPES.map(rt => (
            <button key={rt.value} onClick={() => setActiveType(rt.value)}
              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${activeType === rt.value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              data-testid={`ref-tab-${rt.value}`}>
              {rt.label}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground bg-gray-50 rounded-lg px-4 py-2.5 border" data-testid="ref-legend">
          <span className="flex items-center gap-1.5"><Pencil className="h-3 w-3" /> Modifier</span>
          <span className="flex items-center gap-1.5"><ToggleRight className="h-3.5 w-3.5 text-emerald-500" /> Désactiver / Réactiver</span>
          <span className="flex items-center gap-1.5"><Trash2 className="h-3 w-3 text-red-400" /> Supprimer définitivement <span className="text-gray-400">(usage = 0 uniquement)</span></span>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold">{meta?.label}</h2>
              <p className="text-xs text-muted-foreground">{meta?.desc} — {activeCount} actif{activeCount > 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer" data-testid="show-inactive-toggle">
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="h-3.5 w-3.5 rounded" />
                Afficher inactifs
              </label>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher..." className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
              data-testid="ref-search-input" />
          </div>

          {/* Add */}
          <div className="flex items-center gap-2 flex-wrap max-w-2xl" data-testid="ref-add-form">
            <Input placeholder="Nouvelle valeur..." value={newValue} onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()} className="h-8 text-sm flex-1 min-w-[160px]" disabled={saving} data-testid="ref-add-input" />
            {isTagsProduit && (
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)}
                className="h-8 text-sm border border-gray-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="ref-add-category">
                <option value="">-- Catégorie * --</option>
                {tagCategories.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            )}
            <Button size="sm" onClick={handleAdd} disabled={!newValue.trim() || saving || (isTagsProduit && !newCategory)} className="h-8" data-testid="ref-add-button">
              Ajouter
            </Button>
          </div>

          {/* Info banner for tags */}
          {isTagsProduit && (
            <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100" data-testid="tags-category-info">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>Chaque tag produit doit avoir une catégorie (Mécanique, Univers, Thème, Gamme...). La catégorie est obligatoire lors de la création et modifiable via le bouton éditer.</span>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Chargement...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ref-table">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left">Valeur {isTagsProduit && '/ Catégorie'}</th>
                    <th className="px-3 py-2 text-center w-20">Produits</th>
                    <th className="px-3 py-2 text-center w-16">Ordre</th>
                    <th className="px-3 py-2 text-center w-16">Actif</th>
                    <th className="px-3 py-2 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(item => (
                    <RefRow key={item.id} item={item} onUpdate={handleUpdate} onToggle={handleToggle}
                      onHardDelete={handleHardDelete} saving={saving}
                      usage={getUsage(item)} isTagsProduit={isTagsProduit} tagCategories={tagCategories} />
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-6">
                  {searchTerm ? 'Aucun résultat' : 'Aucune valeur'}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">{filtered.length} valeur{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
